/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { InfoOutlined } from "@mui/icons-material";
import { Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import React from "react";

import { TestIds } from "../../testIds";
import { formatNumber } from "../../util/helpers";

import { UnitText } from "./components/UnitText";
import { SalesAgreementProductLine } from "./storage";

type Props = Pick<SalesAgreementProductLine, "quantity" | "unit">;

export const SalesLineRemainingBreakdown = ({ quantity, unit }: Props) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ ml: "auto", display: "flex", flexDirection: "row", gap: "24px" }}>
      {quantity.monthRemaining || quantity.monthRemaining === 0 ? (
        <Box sx={{ ml: "auto" }}>
          <UnitText
            sx={{
              textAlign: "end",
              width: { xs: 102, sm: "auto" },
            }}
            label={
              <div style={{ display: "flex", gap: "4px" }}>
                Available this month
                <Tooltip
                  placement="top-end"
                  title="Total available to order this month, including any unused quantity from earlier months."
                  slotProps={{
                    popper: {
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: colors.primary900,
                          color: colors.primary25,
                          fontSize: "0.875rem",
                          lineHeight: "1.125rem",
                          textAlign: "center",
                          padding: "0.625rem 1rem",
                          maxWidth: "20rem",
                        },
                      },
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -8],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <InfoOutlined sx={{ fontSize: "20px", paddingTop: "0.125rem" }} />
                </Tooltip>
              </div>
            }
            unit={`${formatNumber(Math.max(quantity.monthRemaining ?? 0, 0))}${unit}`}
            color="secondary"
            testId={TestIds.feat.agreement.salesLines.monthRemaining}
          />
        </Box>
      ) : null}
      <UnitText
        sx={{
          textAlign: "end",
          width: { xs: 100, sm: "auto" },
        }}
        label={
          isMobile ? (
            <div>
              <div>Remaining</div>
              <div>on agreement</div>
            </div>
          ) : (
            "Remaining on agreement"
          )
        }
        unit={`${formatNumber(quantity.remaining > 0 ? quantity.remaining : 0)}${unit}`}
        color="secondary"
        testId={TestIds.feat.agreement.salesLines.remaining}
      />
    </Box>
  );
};

export default SalesLineRemainingBreakdown;
